import { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { ChevronDown, ChevronRight, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/Collapsible';

interface CollapsibleSectionProps {
  id: string;
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export interface CollapsibleSectionRef {
  expand: () => void;
  collapse: () => void;
  toggle: () => void;
}

const CollapsibleSection = forwardRef<CollapsibleSectionRef, CollapsibleSectionProps>(
  ({ id, icon: Icon, title, subtitle, children, defaultOpen = true }, ref) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    useImperativeHandle(ref, () => ({
      expand: () => setIsOpen(true),
      collapse: () => setIsOpen(false),
      toggle: () => setIsOpen((prev) => !prev),
    }));

    // Handle expand events with callback
    const handleExpandSection = useCallback(
      (event: CustomEvent<{ sectionId: string }>) => {
        if (event.detail.sectionId === id) {
          setIsOpen(true);
        }
      },
      [id]
    );

    // Register event listener
    useEffect(() => {
      window.addEventListener('expandSection', handleExpandSection as EventListener);
      return () => {
        window.removeEventListener('expandSection', handleExpandSection as EventListener);
      };
    }, [handleExpandSection]);

    return (
      <section id={id} className="mb-12">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="w-full group">
            <div className="flex items-center gap-4 mb-6 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="bg-zinc-800/50 p-3 rounded-xl shadow-lg border border-zinc-700">
                <Icon className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-left flex-1">
                <h2 className="text-2xl font-bold text-white tracking-tight">{title}</h2>
                {subtitle && <p className="text-zinc-300 mt-1">{subtitle}</p>}
              </div>
              <div
                className={cn(
                  'p-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 transition-all border border-zinc-700',
                  'group-hover:bg-zinc-700/70'
                )}
              >
                {isOpen ? (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-zinc-400" />
                )}
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
            {children}
          </CollapsibleContent>
        </Collapsible>
      </section>
    );
  }
);

CollapsibleSection.displayName = 'CollapsibleSection';

export default CollapsibleSection;
